// Übersicht „Alte Kundenakten" (01.10.2026) — sieht aus wie der Windows-
// Explorer: eine Zeile je Ordner mit Name, Typ, Größe, Geändert am, und dazu
// die beiden Entscheidungen, um die es hier geht: Zuweisen und Nicht benötigt.
//
// Ohne Fluent, ohne Seitentitel (der steht in der Kopfzeile der App), ohne
// Kopf-Suche und Stift. Die Leiste hat nur das Segment Noch offen | Zugewiesen
// | Nicht benötigt und ein Suchfeld für Ordner. Mit Suchbegriff gilt keine
// Ansicht — gesucht wird über alle Zustände (`ansicht.ts`). „Fehlt am Share"
// erscheint nie. Ersetzt `AlteKundenaktenTable.tsx` samt CommandBar.

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { apiUrl } from "../../utils/api";
import { Chip, DataTable, type Column, type SortState } from "../redesign/table";
import { I } from "../redesign/kvpIcons";
import { FolderIcon } from "../documents/explorer/fileTypeIcons";
import { ZuweisenPopup } from "./ZuweisenPopup";
import { Folder, Stats, alleKontaktNamen, humanSize, kontaktKurz } from "./types";
import { ANSICHTEN, type Ansicht, istZugewiesen, passtZurAnsicht } from "./ansicht";

const PAGE_SIZE = 50;

const fmtDatum = (s?: string | null): string => (s ? new Date(s).toLocaleDateString("de-DE") : "—");
const fmtDatumLang = (s?: string | null): string => (s ? new Date(s).toLocaleString("de-DE") : "Noch nicht gescannt");

/** Was in der Spalte „Zuweisen" neben dem Kontakt steht: Buchung läuft / Fehler. */
const buchungsHinweis = (f: Folder): { text: string; fehler?: boolean } | null => {
  if (f.importError) return { text: "Fehler beim Buchen", fehler: true };
  if (istZugewiesen(f) && !f.importedAt) return { text: "wird gebucht …" };
  return null;
};

export const AlteKundenaktenSeite: React.FC = () => {
  const navigate = useNavigate();
  const [ansicht, setAnsicht] = useState<Ansicht>("offen");
  const [suche, setSuche] = useState("");
  const [rows, setRows] = useState<Folder[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats | null>(null);
  const [sort, setSort] = useState<SortState | null>(null);
  const [assigning, setAssigning] = useState<Folder | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  // Generation der Liste: eine späte Antwort einer alten Suche darf eine neue
  // nicht überschreiben — der Grund für die früher „eingefrorene" Trefferzahl.
  const generation = useRef(0);

  const params = useCallback(
    (offset: number): Record<string, string> => ({
      limit: String(PAGE_SIZE),
      offset: String(offset),
      q: suche.trim(),
      ansicht,
      ...(sort ? { sort: sort.field, dir: sort.direction } : {}),
    }),
    [suche, ansicht, sort],
  );

  const loadStats = useCallback(() => {
    fetch(apiUrl("kundenakten-folders/stats"))
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => s && setStats(s))
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Erste Seite: 250 ms Entprellung, Abbruch beim nächsten Wechsel.
  useEffect(() => {
    const gen = ++generation.current;
    const ctrl = new AbortController();
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(apiUrl("kundenakten-folders", params(0)), { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const tot = res.headers.get("X-Total-Count") ?? res.headers.get("x-total-count");
        const data = await res.json();
        if (gen !== generation.current) return;
        const arr: Folder[] = Array.isArray(data) ? data : [];
        setRows(arr);
        setHasMore(arr.length === PAGE_SIZE);
        setTotal(tot != null ? Number(tot) || 0 : arr.length);
        setFehler(null);
      } catch (e) {
        if ((e as { name?: string })?.name === "AbortError" || gen !== generation.current) return;
        setRows([]);
        setHasMore(false);
        setTotal(null);
        setFehler("Die Ordner konnten nicht geladen werden.");
      } finally {
        if (gen === generation.current) setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [params]);

  const loadMore = useCallback(async () => {
    const gen = generation.current;
    try {
      const res = await fetch(apiUrl("kundenakten-folders", params(rows.length)));
      if (!res.ok) {
        setHasMore(false);
        return;
      }
      const data = await res.json();
      if (gen !== generation.current) return;
      const arr: Folder[] = Array.isArray(data) ? data : [];
      setRows((p) => [...p, ...arr]);
      if (arr.length < PAGE_SIZE) setHasMore(false);
    } catch {
      setHasMore(false);
    }
  }, [params, rows.length]);

  /** Zeile nach einer Aktion übernehmen; fällt sie aus der Ansicht, auch aus der Zahl. */
  const zeileAktualisieren = useCallback(
    (f: Folder) => {
      const bleibt = passtZurAnsicht(ansicht, suche, f);
      const warDa = rows.some((r) => r.id === f.id);
      setRows((p) => p.map((r) => (r.id === f.id ? { ...r, ...f } : r)).filter((r) => r.id !== f.id || bleibt));
      if (warDa && !bleibt) setTotal((t) => (t == null ? t : Math.max(0, t - 1)));
      loadStats();
    },
    [ansicht, suche, rows, loadStats],
  );

  const post = useCallback(
    async (id: number, pfad: string) => {
      try {
        const res = await fetch(apiUrl(`kundenakten-folders/${id}/${pfad}`), { method: "POST" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        zeileAktualisieren(await res.json());
      } catch {
        setFehler("Die Änderung konnte nicht gespeichert werden.");
      }
    },
    [zeileAktualisieren],
  );

  const columns: Column<Folder>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        sortable: true,
        sortField: "name",
        className: "name",
        minWidth: 280,
        cell: (r) => (
          <span className="ak-name" title={r.folderName}>
            <FolderIcon size={18} />
            <span className="ak-name-text">{r.folderName}</span>
          </span>
        ),
      },
      { id: "typ", header: "Typ", width: 120, cell: () => <span className="ak-muted">Dateiordner</span> },
      {
        id: "groesse",
        header: "Größe",
        width: 100,
        align: "right",
        cell: (r) => <span title={`${r.fileCount ?? 0} Dateien`}>{humanSize(r.sizeBytes)}</span>,
      },
      {
        id: "datum",
        header: "Geändert am",
        width: 130,
        sortable: true,
        sortField: "datum",
        cell: (r) => <span title={fmtDatumLang(r.lastModifiedAt)}>{fmtDatum(r.lastModifiedAt)}</span>,
      },
      {
        id: "zuweisen",
        header: "Zuweisen",
        width: 320,
        cell: (r) => {
          const namen = alleKontaktNamen(r);
          const hinweis = buchungsHinweis(r);
          if (!namen.length) {
            return (
              <span className="ak-zuw" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="ak-rowbtn primary" onClick={() => setAssigning(r)}>
                  Zuweisen
                </button>
              </span>
            );
          }
          return (
            <span className="ak-zuw" onClick={(e) => e.stopPropagation()}>
              <span title={namen.join(" · ")}>
                <Chip tone="chip-kunde">{kontaktKurz(r)}</Chip>
              </span>
              {hinweis && <span className={hinweis.fehler ? "ak-fehler" : "ak-muted"} title={r.importError ?? undefined}>{hinweis.text}</span>}
              <button type="button" className="ak-rowbtn" onClick={() => setAssigning(r)}>
                ändern
              </button>
              <button type="button" className="ak-rowbtn" onClick={() => void post(r.id, "unassign")}>
                aufheben
              </button>
            </span>
          );
        },
      },
      {
        id: "nichtBenoetigt",
        header: "Nicht benötigt",
        width: 160,
        cell: (r) => (
          <span className="ak-zuw" onClick={(e) => e.stopPropagation()}>
            {r.notNeeded ? (
              <button type="button" className="ak-rowbtn" onClick={() => void post(r.id, "needed")}>
                Wieder benötigt
              </button>
            ) : (
              <button type="button" className="ak-rowbtn" onClick={() => void post(r.id, "not-needed")}>
                Nicht benötigt
              </button>
            )}
          </span>
        ),
      },
    ],
    [post],
  );

  const sucheAktiv = suche.trim().length > 0;

  return (
    <div className="kvp-rd kvp-rd-table-page">
      <div className="bar">
        <div className="docx-seg" role="group" aria-label="Ansicht">
          {ANSICHTEN.map((a) => (
            <button
              key={a.key}
              type="button"
              className={"docx-seg-btn" + (ansicht === a.key ? " on" : "")}
              aria-pressed={ansicht === a.key}
              onClick={() => setAnsicht(a.key)}
            >
              {a.label}
              {stats && typeof stats[a.statsKey] === "number" && <span className="ak-zahl">{stats[a.statsKey]}</span>}
            </button>
          ))}
        </div>
        <div className="search" style={{ marginLeft: "auto" }}>
          {I.search}
          <input
            type="search"
            placeholder="Ordner suchen …"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            aria-label="Ordner suchen"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/alte-kundenakten/${r.id}`)}
        emptyText={
          loading
            ? "Lade Ordner …"
            : sucheAktiv
              ? "Kein Ordner passt zur Suche."
              : ansicht === "offen"
                ? "Alles zugewiesen — es gibt nichts mehr zu entscheiden."
                : "Keine Ordner in dieser Ansicht."
        }
        sort={sort}
        onSortChange={setSort}
        onLoadMore={loadMore}
        hasMore={hasMore}
        loading={loading}
        loadingText="Lade Ordner …"
        scrollContainerId="alte-kundenakten-scroll"
        aboveTable={
          <div className="kt-subbar">
            <span className="ak-anzahl">{total == null ? "…" : `${total} Ordner`}</span>
            {sucheAktiv && <span className="ak-hinweis">Suche über alle Ordner, auch zugewiesene und nicht benötigte.</span>}
            {fehler && <span className="ak-fehler">{fehler}</span>}
          </div>
        }
      />

      {assigning && (
        <ZuweisenPopup
          folder={assigning}
          onClose={() => setAssigning(null)}
          onAssigned={(f) => {
            zeileAktualisieren(f);
            setAssigning(null);
          }}
        />
      )}
    </div>
  );
};

export default AlteKundenaktenSeite;
